import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { PilotAssignmentCommands, PilotAssignmentErrorCodes } from '../../services/DispatcherActions';
import Spacer from '../../../../components/layout/Spacer';
import { errorsContainsFromObject, isNullOrEmpty } from '../../../../components/helpers/ObjectHelpers';
import { LocationBoardingTypeCard } from './location/LocationBoardingTypeCard';
import { LocationPilotTypeAddLocationCard } from './location/LocationPilotTypeAddLocationCard';
import { LocationStartStopCard } from './location/LocationStartStopCard';
import { getPilotAssignmentStatusColor } from '../helpers/PilotAssignmentHelpers';
import { Card } from '../../../../components/layout/card/Card';
import { useHasEditableChanged } from '../hooks/usePilotAssignmentStore';

export const LocationCard = (
    {
        ref,
        index,
        assignmentService,
        isEditable = false,
        codeStrings = [],
        onAfterChange,
        onDelete,
        onSearch
    }) => {

    useImperativeHandle(ref, () => ({
        onLocationsChanged() {
            initializeAsync();
        },
        onInitializeAsync() {
            initializeAsync();
            if (isNullOrEmpty(pilotTypeRef.current)) return;
            pilotTypeRef.current.onInitialize();
        },
    }));

    const pilotTypeRef = useRef();

    const location = assignmentService.getLocationByIndex(index);
    const isFirst = index === 0;
    const isLast = index === assignmentService.locationsCount - 1;
    const borderColor = getPilotAssignmentStatusColor(assignmentService.assignment);

    const hasEditableChanged = useHasEditableChanged();
    
    const [validationErrors, setValidationErrors] = useState({
        pilotType: false,
        stopTime: false,
        startTime: false,
        boardingType: false
    });

    const [isDirty, setIsDirty] = useState(false);

    // Memoize error code objects to prevent unnecessary re-creation
    const startTimeTargetErrorCodes = useMemo(() => ({
        PilotLocationArrivalTimeExistsPreviousDepartureTimeIsMissing: `${PilotAssignmentErrorCodes.PilotLocationArrivalTimeExistsPreviousDepartureTimeIsMissing}_${location.sequenceNo}`,
        PilotLocationArrivalTimeCannotBeInTheFuture: `${PilotAssignmentErrorCodes.PilotLocationArrivalTimeCannotBeInTheFuture}_${location.sequenceNo}`
    }), [location.sequenceNo]);

    const stopTimeTargetErrorCodes = useMemo(() => ({
        PilotLocationPrevousArrivalTimeExistsDepartureTimeIsMissing: `${PilotAssignmentErrorCodes.PilotLocationPrevousArrivalTimeExistsDepartureTimeIsMissing}_${location.sequenceNo}`,
        PilotLocationDepartureTimeMustBeAfterPreviousDepartureTime: `${PilotAssignmentErrorCodes.PilotLocationDepartureTimeMustBeAfterPreviousDepartureTime}_${location.sequenceNo}`,
        PilotLocationDepartureTimeMustBeAfterPreviousArrivalTime: `${PilotAssignmentErrorCodes.PilotLocationDepartureTimeMustBeAfterPreviousArrivalTime}_${location.sequenceNo}`,
        PilotLocationDepartureTimeCannotBeInTheFuture: `${PilotAssignmentErrorCodes.PilotLocationDepartureTimeCannotBeInTheFuture}_${location.sequenceNo}`
    }), [location.sequenceNo]);

    const boardingTypeTargetErrorCodes = useMemo(() => ({
        PilotLocationOffboardingNotPossible: `${PilotAssignmentErrorCodes.PilotLocationOffboardingNotPossible}_${location.sequenceNo}`
    }), [location.sequenceNo]);

    const pilotTypeTargetErrorCodes = useMemo(() => ({
        PilotLocationPilotTypeIsMissing: `${PilotAssignmentErrorCodes.PilotLocationPilotTypeIsMissing}_${location.sequenceNo}`,
    }), [location.sequenceNo]);

    useEffect(() => {
        const newErrors = {
            stopTime: errorsContainsFromObject(codeStrings, stopTimeTargetErrorCodes),
            startTime: errorsContainsFromObject(codeStrings, startTimeTargetErrorCodes),
            boardingType: errorsContainsFromObject(codeStrings, boardingTypeTargetErrorCodes),
            pilotType: errorsContainsFromObject(codeStrings, pilotTypeTargetErrorCodes)
        };
        
        setValidationErrors(newErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeStrings, startTimeTargetErrorCodes, stopTimeTargetErrorCodes, boardingTypeTargetErrorCodes, pilotTypeTargetErrorCodes])

    useEffect(() => {
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!hasEditableChanged) return;
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEditableChanged]);

    return (
        <>
            {
                !isFirst &&
                <>
                    <LocationPilotTypeAddLocationCard
                        ref={pilotTypeRef}
                        key={`pilot-type-${location.locationId}-${validationErrors.pilotType}`}
                        index={index}
                        assignmentService={assignmentService}
                        onAfterChange={handleOnAfterChange}
                        isEditable={isEditable}
                        hasPilotTypeValidationError={validationErrors.pilotType}
                        onSearchLocationsClick={onSearchLocationsClick}
                    />
                    <Spacer height={15} />
                </>
            }

            <Card
                title={location.name}
                showIndicator={true}
                borderColor={borderColor}
                indicatorPrimaryColor={borderColor}
                isDirty={isDirty}
                hasValidationError={(validationErrors.stopTime || validationErrors.startTime || validationErrors.boardingType)}
                canDelete={(!isFirst && !isLast && isEditable)}
                onDelete={() => onDelete(location)}
                confirmDelete={true}
                confirmDeleteTitle="Fjern lokasjon"
                confirmDeleteText={`&Oslash;nsker du &aring; fjerne lokasjonen <b>${location.name}</b>`}>
                <div className={`pp-location-details-card-body`}>

                    <div className="pp-time-1">
                        {
                            isFirst ?
                                <LocationStartStopCard
                                    isEditable={isEditable}
                                    title="Start"
                                    subTitle="Velg start tid"
                                    time={location.fromTime}
                                    hasValidationError={validationErrors.startTime}
                                    onChange={onDateStartTimePickerChanged}
                                />
                                :
                                <LocationStartStopCard
                                    isEditable={isEditable}
                                    title="Stopp"
                                    subTitle="Velg stopp tid"
                                    time={location.toTime}
                                    hasValidationError={validationErrors.stopTime}
                                    onChange={onDateStopTimePickerChanged}
                                />
                        }
                    </div>

                    {
                        (!isFirst && !isLast) &&
                        <div className="pp-time-2">
                            <LocationStartStopCard
                                isEditable={isEditable}
                                title="Start"
                                subTitle="Velg start tid"
                                time={location.fromTime}
                                hasValidationError={validationErrors.startTime}
                                onChange={onDateStartTimePickerChanged}
                            />
                        </div>
                    }

                    <div className="pp-boarding-type">
                        <LocationBoardingTypeCard
                            index={index}
                            assignmentService={assignmentService}
                            onAfterChange={handleOnAfterChange}
                            isEditable={isEditable}
                            hasValidationError={validationErrors.boardingType}
                        />
                    </div>

                </div>
            </Card>
        </>
    )

    async function initializeAsync() {
        initializeDirty();
    }

    function initializeDirty() {
        const hasAddLocationCmd = assignmentService.hasLocationCommand(location, PilotAssignmentCommands.AddLocation);
        const hasArrivalTimeCommand = assignmentService.hasLocationCommand(location, PilotAssignmentCommands.UpdateLocationArrivalTime);
        const hasDepartureTimeCommand = assignmentService.hasLocationCommand(location, PilotAssignmentCommands.UpdateLocationDepartureTime);
        const hasBoardingTypeCommand = assignmentService.hasLocationCommand(location, PilotAssignmentCommands.UpdateLocationBoardingType);

        if (isFirst) {
            setIsDirty(hasDepartureTimeCommand || hasBoardingTypeCommand);
        } else if (isLast) {
            setIsDirty(hasArrivalTimeCommand || hasBoardingTypeCommand);
        } else {
            setIsDirty(hasAddLocationCmd || hasDepartureTimeCommand || hasArrivalTimeCommand || hasBoardingTypeCommand);
        }
    }

    async function onDateStartTimePickerChanged(e) {
        if (!isEditable) return;
        const obj = {
            location: location,
            departureTime: e,
            isPilotBoardingTypeOffBoarding: !isFirst
        }
        
        await assignmentService.onUpdateLocationDepartureTimeAsync(obj, location.pilotBoardingType);
        onAfterChange();
    }

    async function onDateStopTimePickerChanged(e) {
        if (!isEditable) return;
        const obj = {
            location: location,
            arrivalTime: e,
            isPilotBoardingTypeOffBoarding: !isFirst
        }

        await assignmentService.onUpdateLocationArrivalTimeAsync(obj, location.pilotBoardingType);
        onAfterChange();
    }

    function onSearchLocationsClick() {
        if (!isEditable) return;

        onSearch(location);
    }

    function handleOnAfterChange() {
        initializeDirty();
        onAfterChange();
    }
}
